import React, { useMemo } from 'react';
import { Paper, Typography, Stack, Button, Box } from '@mui/material';
import Chart from 'react-apexcharts';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { exportCsvWithDate } from './csvUtil';

function escapeHtml(str) {
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const CHART_HEIGHT = 240;

export default function Scores1500Report({ scores1500Rows = [], eventsMap = {}, teams = {}, eventHasAdjustments }) {
  const totalCount = (scores1500Rows || []).length;

  const columns = [
    { field: 'eventNumber', headerName: 'Event #', width: 100 },
    { field: 'eventName', headerName: 'Event Name', width: 280 },
    { field: 'teamNumber', headerName: 'Team #', width: 100 },
    { field: 'teamName', headerName: 'Team Name', width: 220 },
    { field: 'finalScore', headerName: 'Score', width: 120, renderCell: (params) => {
      const v = params.value;
      const n = Number(v);
      if (!Number.isFinite(n)) return v || '';
      // convert seconds to mm:ss for readability
      const minutes = Math.floor(n / 60);
      const seconds = Math.floor(n % 60);
      const pad = (s) => String(s).padStart(2, '0');
      return `${minutes}:${pad(seconds)}`;
    } },
    { field: 'updatedAt', headerName: 'Updated At', width: 200 },
    { field: 'affiliated', headerName: 'Affiliated', width: 110, renderCell: (params) => params.value ? <CheckCircleIcon sx={{ color: 'green' }} /> : <CancelIcon sx={{ color: 'red' }} /> },
  ];

  const countsByEvent = useMemo(() => {
    const map = {};
    (scores1500Rows || []).forEach(r => {
      const k = r.eventUid || `${r.eventName}`;
      if (!map[k]) map[k] = { name: r.eventName || (eventsMap[r.eventUid] && eventsMap[r.eventUid].name) || k, count: 0 };
      map[k].count++;
    });
    const arr = Object.keys(map).map(k => ({ eventUid: k, eventName: map[k].name, count: map[k].count }));
    arr.sort((a,b) => b.count - a.count);
    return arr.slice(0, 10);
  }, [scores1500Rows, eventsMap]);

  const chartOptions = useMemo(() => ({
    chart: { id: 'scores1500-top10', toolbar: { show: true }, animations: { enabled: false } },
    plotOptions: { bar: { horizontal: false, columnWidth: '60%' } },
    xaxis: { categories: countsByEvent.map(c => c.eventName), labels: { rotate: -45, hideOverlappingLabels: true } },
    yaxis: { title: { text: 'Count' } },
    dataLabels: { enabled: false },
    tooltip: {
      enabled: true,
      shared: false,
      followCursor: false,
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const labels = (w.config.xaxis && w.config.xaxis.categories) || [];
        const label = labels[dataPointIndex] || '';
        const val = series[seriesIndex] && series[seriesIndex][dataPointIndex] !== undefined ? series[seriesIndex][dataPointIndex] : 0;
        return `<div style="pointer-events:none;padding:8px;font-size:13px;"><strong>${escapeHtml(label)}</strong><div style="margin-top:6px">Count: <strong>${val}</strong></div></div>`;
      }
    }
  }), [countsByEvent]);

  const chartSeries = [{ name: 'Scores = 1500', data: countsByEvent.map(c => c.count) }];

  const handleExportRows = () => {
    const headers = ['eventNumber','eventName','teamNumber','teamName','finalScore','updatedAt','affiliated','scoreUid'];
    const rows = (scores1500Rows || []).map(r => ({
      eventNumber: r.eventNumber || '',
      eventName: r.eventName || '',
      teamNumber: r.teamNumber || '',
      teamName: r.teamName || '',
      finalScore: r.finalScore || '',
      updatedAt: r.updatedAt || '',
      affiliated: r.affiliated ? 'true' : 'false',
      scoreUid: r.scoreUid || ''
    }));
    exportCsvWithDate('scores-equal-1500', headers, rows);
  };

  const handleExportHistogram = () => {
    const headers = ['eventUid','eventName','count'];
    const rows = countsByEvent.map(c => ({ eventUid: c.eventUid, eventName: c.eventName, count: c.count }));
    exportCsvWithDate('scores-equal-1500-top10', headers, rows);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1">Scores = 1500</Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={handleExportHistogram}>Export Top 10</Button>
            <Button size="small" variant="contained" onClick={handleExportRows}>Export Rows</Button>
          </Stack>
        </Stack>
        <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">Total: {totalCount}</Typography>
            <Typography variant="body2">All scores where final score equals 1500 (excluding scoreMax marker rows)</Typography>
          </Box>
          <Box sx={{ width: 600 }}>
            <Chart type="bar" series={chartSeries} options={chartOptions} height={CHART_HEIGHT} />
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 1 }}>
        <div style={{ height: 520, width: '100%' }}>
          <DataGrid
            rows={scores1500Rows}
            columns={columns}
            slots={{ toolbar: GridToolbar }}
            pageSizeOptions={[25,50,100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        </div>
      </Paper>
    </Box>
  );
}
